import { useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";

interface FormState {
  name: string;
  email: string;
  message: string;
}

const contactInfo = [
  { icon: "ri-mail-line", label: "内容合作邮箱", value: "contact@heiliaovideo.cn" },
  { icon: "ri-customer-service-2-line", label: "客服热线", value: "400-660-8899" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "北京市朝阳区传媒创意园 A 座 12 层" },
  { icon: "ri-time-line", label: "工作时间", value: "周一至周五 09:00 - 18:00" },
];

export default function Contact() {
  usePageMeta({
    title: "联系我们 - 黑料视频",
    description:
      "通过联系表单或邮箱、电话与黑料视频团队取得联系，提供娱乐资讯线索或洽谈内容合作。",
    keywords: "黑料视频,联系我们,内容合作,资讯线索",
  });

  const [form, setForm] = useState<FormState>({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [sent, setSent] = useState(false);

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
    setSent(false);
  };

  const validate = (): boolean => {
    const next: Partial<FormState> = {};
    if (!form.name.trim()) next.name = "请填写你的称呼";
    if (!form.email.trim()) next.email = "请填写邮箱地址";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = "邮箱格式好像不太对，请检查一下";
    if (!form.message.trim()) next.message = "留言内容不能为空";
    else if (form.message.length > 500) next.message = "留言内容请控制在 500 字以内";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const honeypot = new FormData(event.currentTarget).get("company_alt");
    if (typeof honeypot === "string" && honeypot.trim()) {
      setSent(true);
      return;
    }
    if (!validate()) return;
    setSent(true);
  };

  return (
    <div className="w-full">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            联系我们
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            有想看的选题、想纠正的信息，或者内容合作的想法？填写下面的表单或直接通过邮箱联系我们，我们会认真阅读每一条留言。
          </p>
        </div>
      </section>

      <section className="w-full">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Form */}
            <div className="lg:col-span-2">
              <form
                id="contact-form"
                onSubmit={handleSubmit}
                noValidate
                className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
              >
                <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950">
                  <i className="ri-edit-2-line text-primary-500"></i>
                  留言给我们
                </h2>
                <p className="mt-2 text-xs text-foreground-500">
                  带 <span className="text-primary-500">*</span> 的为必填项。
                </p>

                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="name" className="text-sm font-medium text-foreground-800">
                      你的称呼 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="怎么称呼你？"
                      className={`rounded-md border bg-background-100 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 ${
                        errors.name ? "border-primary-500" : "border-background-300"
                      }`}
                    />
                    {errors.name ? (
                      <span className="text-xs text-primary-600">{errors.name}</span>
                    ) : null}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-sm font-medium text-foreground-800">
                      邮箱地址 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="方便我们回复你"
                      className={`rounded-md border bg-background-100 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 ${
                        errors.email ? "border-primary-500" : "border-background-300"
                      }`}
                    />
                    {errors.email ? (
                      <span className="text-xs text-primary-600">{errors.email}</span>
                    ) : null}
                  </div>
                </div>

                <div className="mt-5 flex flex-col gap-1.5">
                  <label htmlFor="message" className="text-sm font-medium text-foreground-800">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    maxLength={500}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="想告诉我们什么？选题建议、线索或合作意向都可以写在这里（最多 500 字）"
                    className={`resize-none rounded-md border bg-background-100 px-3 py-2.5 text-sm leading-relaxed text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 ${
                      errors.message ? "border-primary-500" : "border-background-300"
                    }`}
                  ></textarea>
                  <div className="flex items-center justify-between">
                    {errors.message ? (
                      <span className="text-xs text-primary-600">{errors.message}</span>
                    ) : (
                      <span className="text-xs text-foreground-400">
                        我们通常会在 2 个工作日内回复
                      </span>
                    )}
                    <span className="text-xs text-foreground-400">
                      {form.message.length}/500
                    </span>
                  </div>
                </div>

                {/* anti-spam honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-extra-field"
                />

                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="submit"
                    className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-fill"></i>
                    提交留言
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setForm({ name: "", email: "", message: "" });
                      setErrors({});
                      setSent(false);
                    }}
                    className="flex items-center justify-center gap-2 rounded-md border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line"></i>
                    清空重填
                  </button>
                </div>

                {sent ? (
                  <div className="mt-5 flex items-start gap-3 rounded-md border border-accent-300 bg-accent-50 px-4 py-3.5">
                    <i className="ri-checkbox-circle-fill mt-0.5 text-lg text-accent-600"></i>
                    <div>
                      <p className="text-sm font-medium text-accent-900">
                        表单校验通过，感谢你的留言！
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-accent-800">
                        当前为演示站点，表单不会真实发送或存储任何数据。正式上线后，这里会真正把内容送到我们的邮箱。
                      </p>
                    </div>
                  </div>
                ) : null}
              </form>
            </div>

            {/* Contact info */}
            <aside className="lg:col-span-1">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950">
                  <i className="ri-contacts-book-line text-primary-500"></i>
                  常用联系方式
                </h2>
                <ul className="mt-4 flex flex-col gap-4">
                  {contactInfo.map((item) => (
                    <li key={item.label} className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-500">
                        <i className={`${item.icon} text-lg`}></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500">{item.label}</p>
                        <p className="mt-0.5 text-sm font-medium text-foreground-900">
                          {item.value}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 overflow-hidden rounded-lg border border-background-200">
                <iframe
                  title="黑料视频办公地址地图"
                  src="https://www.google.com/maps?q=Beijing%20Chaoyang%20District%20Media%20Creative%20Park&output=embed"
                  className="h-64 w-full border-0"
                  loading="lazy"
                ></iframe>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}